import React, { useState } from 'react';
import { Button, Card, Col, Row, Space } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import isObject from 'lodash/isObject';
import { RiArrowLeftLine } from 'react-icons/ri';
import { MdOutlineUnpublished } from 'react-icons/md';
import IntlMessages from '../../../../../../layout/components/lang/IntlMessages';
import ActionButton from '../../../../../../layout/components/action-button';
import { HasRights } from '../../../../../../components/HasRights/HasRights';
import { Permissions } from '../../../../../../const/permissions';
import handleResponse from '../../../../../../lib/handleResponse';
import transformErrors from '../../../../../../lib/transformErrors';
import BreadCrumbs from '../../../../../../layout/components/breadcrumbs';
import urlPagePushNotifications from '../../../../../../urls/urlPagePushNotifications';
import urlPagePushNotificationsSchedule from '../../../../../../urls/urlPagePushNotificationsSchedule';
import urlPagePushNotificationScheduleDetails from '../../../../../../urls/urlPagePushNotificationScheduleDetails';
import FormCreateEditPushNotificationSchedule from '../../../../forms/FormCreateEditPushNotificationSchedule';
import {
  useGetSpecificPushNotificationScheduleQuery,
  useUnpublishPushNotificationScheduleMutation,
  useUpdatePushNotificationScheduleMutation,
} from '../../../../api/pushNotificationsScheduleApiSlice';



const PageEditPushNotificationSchedule = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { scheduleId } = useParams();
  const [ formErrors, setFormErrors ] = useState({});

  const [ updatePushNotificationSchedule, { isLoading: isUpdatingPushNotificationSchedule } ] = useUpdatePushNotificationScheduleMutation();

  const { data: schedule = {}, isFetching } = useGetSpecificPushNotificationScheduleQuery({ scheduleId });

  const [ unpublishPushNotificationSchedule, { isLoading: isUnpublishPushNotificationScheduleLoading } ] = useUnpublishPushNotificationScheduleMutation();

  const handleUnpublish = () => {
    unpublishPushNotificationSchedule(scheduleId)
      .unwrap()
      .then(() => handleResponse('success', intl, 'ui-general-unpublished-successfully'))
      .catch((error) => handleResponse('error', intl, 'ui-general-unpublish-failed', [], error));
  };

  const onSubmit = (data) => {
    updatePushNotificationSchedule({ id: scheduleId, values: data })
      .unwrap()
      .then(() => {
        handleResponse(
          'success',
          intl,
          'ui-general-updated-successfully',
          [ () => navigate(urlPagePushNotificationsSchedule()) ],
        );
      })
      .catch((error) => {
        handleResponse(
          'error',
          intl,
          'ui-general-update-failed',
          [ () => {
            if (isObject(error?.data?.errors)) {
              const formikInitialErrors = transformErrors(error?.data?.errors);

              setFormErrors(formikInitialErrors);
            }
          } ],
          error,
        );
      });
  };

  return (
    <>
      <Row
        gutter={[ 32, 32 ]}
        justify="space-between"
        className="hp-mb-32"
        align="middle"
      >
        <BreadCrumbs
          breadCrumbParent={
            <Link to={urlPagePushNotifications()}>
              <IntlMessages id='push-notifications-breadcrumbs' />
            </Link>
          }
          breadCrumbParent2={
            <Link to={urlPagePushNotificationsSchedule()}>
              <IntlMessages id='push-notifications-schedule-breadcrumbs' />
            </Link>
          }
          breadCrumbParent3={
            <Link to={urlPagePushNotificationScheduleDetails({ scheduleId })}>
              {scheduleId}
            </Link>
          }
          breadCrumbActive={<IntlMessages id='ui-general-edit' />}
        />

        <Col md={12} span={24}>
          <Row
            gutter={[ 8, 8 ]}
            justify="end"
            align="middle"
          >
            {Boolean(schedule?.is_published) && HasRights([ Permissions.OTHER.NOTIFICATIONS.PUSH.SCHEDULE.UNPUBLISH ]) && (
              <Col>
                <Space>
                  <ActionButton
                    loading={isUnpublishPushNotificationScheduleLoading}
                    title={intl.formatMessage({ id: 'ui-general-unpublish' })}
                    icon={<MdOutlineUnpublished className="hp-mr-8" size={18} />}
                    onClick={handleUnpublish}
                  />
                </Space>
              </Col>
            )}
            <Col>
              <Space>
                <Link to={urlPagePushNotificationsSchedule()}>
                  <Button
                    type="primary"
                    className="hp-mr-sm-8"
                    ghost="true"
                    icon={<RiArrowLeftLine className="hp-mr-8" size={18} />}
                  >
                    <IntlMessages id='ui-general-back-to-list' />
                  </Button>
                </Link>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={[ 32, 32 ]}>
        <Col span={24}>
          <Card className="hp-border-color-black-40 hp-card-6">
            <FormCreateEditPushNotificationSchedule
              initialErrors={formErrors}
              initialValues={{
                push_notification_id: schedule?.push_notification?.data?.id ?? null,
                is_published: false,
                scheduled_at: schedule?.scheduled_at,
                use_utc_zero_time: schedule?.use_utc_zero_time,
                min_version: schedule?.min_version,
                max_version: schedule?.max_version,
                platform: schedule?.platform,
                countries: schedule?.countries,
                languages: schedule?.languages,
                published_at: schedule?.published_at ?? null,
                updated_at: schedule?.updated_at,
              }}
              isEdit
              isSubmitting={isFetching || isUpdatingPushNotificationSchedule}
              onSubmit={onSubmit}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PageEditPushNotificationSchedule;
